import { fg } from '@atlaskit/platform-feature-flags';
import { type ActivityItemEdge } from '@atlassian/search-client/recent-activities/types';
import {
	type ConfluencePage,
	type ExternalDocument,
	type RecommendedDocument,
} from '@atlassian/search-client/recommended-documents/types';
import {
	type RecommendedObject,
	type SmartsRecommendedObjectData,
} from '@atlassian/search-client/user-documents/types';

import type {
	ActivityEnrichment,
	EnrichedRecommendedDocument,
	QuickFindResult,
} from '../../../../common/ui/quick-find/types';

/**
 * Enriches user documents (recommendations) with activity data from
 * the Activity Platform when the same document exists in the recent
 * activities data (matched by ID).
 *
 * The first matching activity per document is used. The rendering layer
 * decides how to use the enriched data (e.g. which timestamp to display).
 *
 * Returns a **new** array of QuickFindResult<EnrichedRecommendedDocument>.
 * The original `recommendedDocuments` array and its items are never mutated.
 */
export const enrichRecommendedDocumentsWithActivity = (
	recommendations: QuickFindResult<RecommendedObject | RecommendedDocument>[],
	recentActivities: QuickFindResult<ActivityItemEdge>[],
): QuickFindResult<EnrichedRecommendedDocument>[] => {
	if (
		recommendations.length === 0 ||
		recentActivities.length === 0 ||
		!fg('qf_override_stale_recommendation_timestamp')
	) {
		return recommendations;
	}

	// Build a lookup map: thirdPartyAri → activity enrichment data
	const activityEnrichmentMap = new Map<string, ActivityEnrichment>();

	for (const activity of recentActivities) {
		const activityObject = activity.data.node.object;
		// object.id is a thirdPartyAri (e.g, "ari:third-party:microsoft.sharepoint::document/driveId/..."),
		const thirdPartyAri = activityObject.id;

		if (!thirdPartyAri) {
			continue;
		}

		const event = activity.data.node.event;

		const enrichment: ActivityEnrichment = {
			timestamp: event.timestamp,
			eventType: event.eventType,
		};

		// Use the first matching activity for each document
		if (!activityEnrichmentMap.has(thirdPartyAri)) {
			activityEnrichmentMap.set(thirdPartyAri, enrichment);
		}
	}

	return recommendations.map((recommendation) => {
		const activityEnrichment = activityEnrichmentMap.get(recommendation.id);
		if (!activityEnrichment) {
			return recommendation;
		}

		return {
			...recommendation,
			data: {
				...recommendation.data,
				activityEnrichment,
			},
		};
	});
};

export const isConfluenceRecommendation = (
	result: EnrichedRecommendedDocument,
): result is EnrichedRecommendedDocument & {
	object: ConfluencePage | SmartsRecommendedObjectData;
} => {
	return (
		result?.object?.__typename === 'ConfluencePage' ||
		result?.object?.__typename === 'ConfluenceBlogPost'
	);
};

export const isExternalDocumentRecommendation = (
	result: EnrichedRecommendedDocument,
): result is EnrichedRecommendedDocument & { object: ExternalDocument } => {
	return result?.object?.__typename === 'ExternalDocument';
};
